import {
  createTemplateTheme,
  type CodeComponentTemplateBranding,
} from './branding';

export function createLocationsMapSource(
  branding: CodeComponentTemplateBranding = {}
): string {
  const {themeSource, primaryColor} = createTemplateTheme(branding, '#2563eb');

  return `import { declareComponent } from "@webflow/react";
import { props } from "@webflow/data-types";
import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
// The bundler mounts this CSS in the Shadow DOM, but the type checker has no
// declaration for package CSS imports.
// @ts-ignore
import "leaflet/dist/leaflet.css";

${themeSource}

/* ── locations ── */
const SAMPLE = [
  {
    name: "River North",
    lat: 41.8919,
    lng: -87.6346,
    address: "445 N Clark St, Chicago, IL",
    hours: "5:00pm – 11:00pm daily",
    phone: "(312) 555-0148",
  },
  {
    name: "West Loop",
    lat: 41.8849,
    lng: -87.6636,
    address: "1108 W Randolph St, Chicago, IL",
    hours: "4:30pm – 10:30pm, closed Mon",
    phone: "(312) 555-0192",
  },
  {
    name: "Wicker Park",
    lat: 41.9088,
    lng: -87.6796,
    address: "1524 N Milwaukee Ave, Chicago, IL",
    hours: "5:00pm – 12:00am Thu–Sun",
  },
  {
    name: "Lincoln Square",
    lat: 41.9754,
    lng: -87.6898,
    address: "4747 N Lincoln Ave, Chicago, IL",
    hours: "11:30am – 9:00pm daily",
    phone: "(773) 555-0110",
  },
  {
    name: "Hyde Park",
    lat: 41.7943,
    lng: -87.5907,
    address: "1372 E 53rd St, Chicago, IL",
    hours: "5:00pm – 10:00pm, closed Tue",
  },
];

function prefersReducedMotion() {
  return Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
}

// Pin markup is raw HTML, so only accept values that parse as a CSS color.
function safeColor(color) {
  return typeof CSS !== "undefined" && CSS.supports("color", color) ? color : "#64748b";
}

function pinIcon(active, brandColor) {
  const color = safeColor(brandColor);
  const fill = active ? color : "#64748b";
  const scale = active ? 1.35 : 1;
  const size = Math.round(30 * scale);
  const height = Math.round(40 * scale);
  return L.divIcon({
    className: "",
    iconSize: [size, height],
    iconAnchor: [size / 2, height - 2],
    html: \`<div class="\${active ? "locations-map-pin-active" : ""}" style="
      width:\${size}px;height:\${height}px;
      display:flex;align-items:flex-end;justify-content:center;
      opacity:\${active ? 1 : 0.88};
      transition:transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 280ms;
      filter:drop-shadow(0 \${active ? 4 : 1}px \${active ? 8 : 2}px rgba(15,23,42,\${active ? 0.3 : 0.15}));
    ">
      <svg width="\${size}" height="\${height}" viewBox="-16 -30 32 42" aria-hidden="true">
        \${active ? \`<circle cx="0" cy="0" r="16" fill="\${color}" opacity="0.15" />\` : ""}
        <circle cx="0" cy="0" r="12" fill="\${color}" opacity="\${active ? 0.25 : 0.08}" />
        <path d="M0,-28 C8,-28 15,-21 15,-13 C15,-3 0,13 0,13 C0,13 -15,-3 -15,-13 C-15,-21 -8,-28 0,-28 Z"
          fill="\${fill}" stroke="#ffffff" stroke-width="\${active ? 2.2 : 1.2}" />
        <circle cx="0" cy="-13" r="\${active ? 5 : 4.5}" fill="#ffffff" />
      </svg>
    </div>\`,
  });
}

function fitBoundsOptions() {
  return { padding: [48, 48], maxZoom: 13, animate: !prefersReducedMotion(), duration: 0.45 };
}

/* ── main component ── */
export function LocationsMap({ primaryColor = ${primaryColor} }) {
  const locations = SAMPLE;
  const [activeIndex, setActiveIndex] = useState(null);

  const mapPaneRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const syncMarkerStyles = useCallback(
    (index) => {
      markersRef.current.forEach((marker, i) => {
        const active = index != null && i === index;
        const dimmed = index != null && i !== index;
        marker.setIcon(pinIcon(active, primaryColor));
        marker.setOpacity(dimmed ? 0.3 : 1);
        marker.setZIndexOffset(active ? 1000 : 0);
      });
    },
    [primaryColor]
  );

  const selectLocation = useCallback(
    (i) => {
      setActiveIndex(i);
      syncMarkerStyles(i);
      const loc = locations[i];
      const map = mapRef.current;
      if (!loc || !map) return;
      map.flyTo([loc.lat, loc.lng], Math.max(map.getZoom(), 13), {
        animate: !prefersReducedMotion(),
        duration: 0.45,
      });
    },
    [locations, syncMarkerStyles]
  );

  const selectLocationRef = useRef(selectLocation);
  selectLocationRef.current = selectLocation;

  const resetView = useCallback(() => {
    setActiveIndex(null);
    syncMarkerStyles(null);
    const map = mapRef.current;
    if (!map || !locations.length) return;
    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
    map.flyToBounds(bounds, fitBoundsOptions());
  }, [locations, syncMarkerStyles]);

  const zoomBy = useCallback((delta) => {
    const map = mapRef.current;
    if (!map) return;
    map.setZoom(map.getZoom() + delta);
  }, []);

  useEffect(() => {
    if (!mapPaneRef.current || mapRef.current) return;

    const map = L.map(mapPaneRef.current, {
      zoomControl: false,
      attributionControl: false,
    });

    // Keep the tile credit (required by Esri) but drop the "Leaflet" prefix.
    L.control.attribution({ prefix: false }).addTo(map);

    // Esri Light Gray Canvas — free, no API key, minimal grayscale style.
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors",
        maxNativeZoom: 16,
        maxZoom: 18,
      }
    ).addTo(map);

    const bounds = L.latLngBounds(locations.map((l) => [l.lat, l.lng]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 13 });

    markersRef.current = locations.map((loc, i) => {
      const marker = L.marker([loc.lat, loc.lng], {
        icon: pinIcon(false, primaryColor),
        title: loc.name,
        keyboard: true,
      }).addTo(map);

      marker.bindTooltip(loc.name, {
        direction: "top",
        offset: [0, -36],
        opacity: 1,
        className: "locations-map-tooltip",
      });

      marker.on("click", () => selectLocationRef.current(i));
      return marker;
    });

    mapRef.current = map;

    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);
    const invalidateFrame = requestAnimationFrame(() => map.invalidateSize());

    return () => {
      cancelAnimationFrame(invalidateFrame);
      window.removeEventListener("resize", onResize);
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, [locations]);

  useEffect(() => {
    syncMarkerStyles(activeIndex);
  }, [activeIndex, syncMarkerStyles]);

  // Shadow DOM ignores Tailwind's @property defaults: set border and outline
  // styles explicitly and avoid shadow or ring utilities.
  return (
    <div
      className="mx-auto flex max-w-5xl flex-col overflow-hidden rounded-2xl border border-solid border-[color:var(--template-border)] bg-[var(--template-surface)]"
      style={{ ...themeStyle(primaryColor), boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)" }}
    >
      <style>{\`
        @keyframes pulse-pin {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .locations-map-pin-active {
          animation: pulse-pin 2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .locations-map-pin-active { animation: none; }
        }
        .locations-map-tooltip {
          background: var(--template-accent);
          color: var(--template-accent-text, #fff);
          border: none;
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 11px;
          font-weight: 600;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
        }
        .locations-map-tooltip::before {
          border-top-color: var(--template-accent);
        }
        .leaflet-container {
          font: inherit;
          background: var(--template-secondary);
        }
        .leaflet-tile-pane {
          filter: grayscale(1) contrast(0.92) brightness(1.04);
        }
      \`}</style>

      <div className="grid grid-cols-[1fr_300px] max-[992px]:grid-cols-1">
        <div className="relative min-h-[320px] overflow-hidden bg-[var(--template-subtle)]">
          <div ref={mapPaneRef} className="absolute inset-0 z-0 h-full w-full" />

          <div className="pointer-events-none absolute right-3 top-3 z-[1000] flex flex-col gap-1.5">
            {[
              { label: "Zoom in", onClick: () => zoomBy(1), text: "+" },
              { label: "Zoom out", onClick: () => zoomBy(-1), text: "−" },
              { label: "Reset view", onClick: resetView, text: "⤢", title: "Fit all pins" },
            ].map((btn) => (
              <button
                key={btn.label}
                type="button"
                aria-label={btn.label}
                title={btn.title}
                onClick={btn.onClick}
                className={
                  "pointer-events-auto flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-[color:var(--template-border)] bg-[var(--template-surface)] text-sm text-[color:var(--template-ink)] hover:border-[color:var(--template-accent)] hover:text-[color:var(--template-accent)] " +
                  FOCUS_RING
                }
              >
                {btn.text}
              </button>
            ))}
          </div>
        </div>

        <aside className="flex flex-col border-l border-solid border-[color:var(--template-border)] bg-[var(--template-surface)] max-[992px]:border-l-0 max-[992px]:border-t">
          <ul className="m-0 flex flex-1 list-none flex-col divide-y divide-solid divide-[color:var(--template-border)] overflow-y-auto p-0">
            {locations.map((loc, i) => {
              const active = i === activeIndex;
              return (
                <li key={loc.name + "-" + i}>
                  {/* 🎨 BRAND COLOR: Selected location */}
                  <button
                    type="button"
                    onClick={() => selectLocation(i)}
                    aria-pressed={active}
                    className={
                      active
                        ? "relative w-full bg-[var(--template-accent)] px-4 py-3.5 text-left text-[color:var(--template-accent-text,white)] transition-colors focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--template-accent-text,white)]"
                        : "relative w-full px-4 py-3.5 text-left text-[color:var(--template-ink)] transition-colors hover:bg-[var(--template-secondary)] focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--template-accent)]"
                    }
                  >
                    {active && (
                      <span className="absolute bottom-0 left-0 top-0 w-1 bg-[var(--template-accent-text,white)]" />
                    )}
                    {/* NAME - Size: base, Weight: 600 */}
                    <span className={"block text-base font-semibold " + HEADING_FONT}>
                      {loc.name}
                    </span>
                    {/* ADDRESS - Size: sm, Weight: 400 */}
                    {loc.address ? (
                      <span className={active ? "mt-0.5 block text-sm opacity-90" : "mt-0.5 block " + TYPE.small}>
                        {loc.address}
                      </span>
                    ) : null}
                    <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                      {[loc.hours, loc.phone].filter(Boolean).map((t) => (
                        <span key={t} className={active ? "text-sm opacity-80" : TYPE.small}>
                          {t}
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
      </div>
    </div>
  );
}

export default declareComponent(LocationsMap, {
  name: "Locations Map",
  description: "Interactive map with location pins and a list of addresses, hours, and phone numbers.",
  group: "Sections",
  props: {
    primaryColor: props.Text({
      name: "Primary Brand Color",
      defaultValue: ${primaryColor},
    }),
  },
});
`;
}

export const LOCATIONS_MAP_SOURCE = createLocationsMapSource();
